import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, TextField } from '@material-ui/core';
import { useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'icerockdev-admin-toolkit';
import styles from './styles.module.scss';
import { CustomJWTAuthProvider } from '~/auth';
import { MIN_PASSWORD_LENGTH } from '~/pages/user/constants';

export const ResetPassword: FC = () => {
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const matches = useRouteMatch<{ token: string }>();
  const config = useConfig();
  const authProvider = config.auth as CustomJWTAuthProvider;
  const { t } = useTranslation();

  useEffect(() => {
    authProvider.sendAuthPasswordConfirm(matches.params.token);
  }, []);

  const onPasswordChange = useCallback(
    (event) => {
      setPassword(event.target.value);
    },
    [setPassword]
  );

  const onPasswordRepeatChange = useCallback(
    (event) => {
      setPasswordRepeat(event.target.value);
    },
    [setPasswordRepeat]
  );

  const onSubmitCapture = useCallback(
    (event: FormEvent) => {
      event.preventDefault();

      if (!config?.auth?.sendAuthPasswUpdate) return;

      if (password !== passwordRepeat) {
        config.notifications.showError(t("messages:Passwords doesn't match"));
        return;
      }

      if (!authProvider.changePasswordToken) {
        config.notifications.showError(t('messages:Token not valid'));
        return;
      }

      authProvider.sendAuthPasswUpdate(authProvider.changePasswordToken, password);
    },
    [matches.params.token, config, password, passwordRepeat]
  );

  return (
    <div className={styles.wrap}>
      <Helmet>
        <title>{t('Password recovery')}</title>
      </Helmet>

      <h3 className={styles.header}>{t('Enter a new password')}</h3>

      <form noValidate onSubmit={onSubmitCapture}>
        <TextField
          variant="filled"
          margin="normal"
          required
          fullWidth
          id="password"
          label={t('Password')}
          name="password"
          type="password"
          defaultValue={password}
          onChange={onPasswordChange}
          autoFocus
        />

        <TextField
          variant="filled"
          margin="normal"
          required
          fullWidth
          type="password"
          id="password"
          label={t('Password repeat')}
          name="passwordRepeat"
          defaultValue={passwordRepeat}
          onChange={onPasswordRepeatChange}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={
            password.length < MIN_PASSWORD_LENGTH ||
            !password.length ||
            !passwordRepeat.length ||
            password !== passwordRepeat
          }
          className={styles.button}
        >
          {t('Restore')}
        </Button>

        <Button
          type="button"
          component={Link}
          to="/"
          variant="text"
          fullWidth
          className={styles.cancel}
        >
          {t('buttons:Cancel')}
        </Button>
      </form>
    </div>
  );
};
